import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { Buffer } from 'buffer';
import { useFlashMessage } from '../context/FlashMessageContext';
import { API_CONFIG } from '../config/api';
import BiometricCapture from '../components/BiometricCapture';
import BiometricLivenessCapture from '../components/BiometricLivenessCapture';
import { useBiometricEnrollmentMode } from '../hooks/useBiometricEnrollmentMode';
import { getOnboardRegistrationErrorMessage } from '../utils/onboardRegistrationErrors';
import SyberKeyWordmark from '../components/SyberKeyWordmark';
import './Registration.css';

// Helper to convert ArrayBuffer to Hex
function bufferToHex(buffer) {
  return [...new Uint8Array(buffer)]
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

// Helper to convert Base64 to ArrayBuffer
function base64ToArrayBuffer(base64) {
  const binaryString = window.atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

function Registration() {
  const [step, setStep] = useState(1);
  const [registrationId, setRegistrationId] = useState(null);
  const [searchParams] = useSearchParams();
  
  // Step 1: Basic Info (First Name, Grandfather's First Name, Place of Birth)
  const [firstName, setFirstName] = useState('');
  const [grandfatherFirstName, setGrandfatherFirstName] = useState('');
  const [placeOfBirth, setPlaceOfBirth] = useState('');
  const [tenantId, setTenantId] = useState('');
  
  // Step 2: Contact Info (Mother's First Name, Phone Number, Email)
  const [mothersFirstName, setMothersFirstName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  
  // Step 3: Biometric
  const [capturedImage, setCapturedImage] = useState(null);
  const [livenessSessionId, setLivenessSessionId] = useState(null);
  const { loading: modeLoading, useLiveness } = useBiometricEnrollmentMode();

  // UI State
  const [emailMessage, setEmailMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { showSuccess, showError } = useFlashMessage();

  // Extract tenant_id from URL parameters
  useEffect(() => {
    const urlTenantId = searchParams.get('tenant_id');
    if (urlTenantId) {
      setTenantId(urlTenantId);
      console.log('Tenant ID extracted from URL:', urlTenantId);
    }
  }, [searchParams]);

  const isEmailValid = useCallback((email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, []);

  const checkEmailAvailability = useCallback(async (email) => {
    if (!isEmailValid(email)) {
      setEmailMessage('Please enter a valid email format.');
      return false;
    }

    try {
      setEmailMessage('Checking email availability...');
      
      // Use the existing email check API (no encryption needed)
      const response = await axios.post(`${API_CONFIG.BASE_URL}/users/check-email`, {
        email: email.toLowerCase()
      });

      if (response.data.exists) {
        setEmailMessage('✗ This email is already registered. Please use a different email or try logging in.');
        return false;
      } else {
        setEmailMessage('✓ This email is available for registration.');
        return true;
      }
    } catch (error) {
      console.error('Email lookup failed:', error);
      if (error.response?.data?.error === 'missing_email') {
        setEmailMessage('Please enter an email address.');
      } else {
        setEmailMessage('Could not verify email. Please try again.');
      }
      return false;
    }
  }, [isEmailValid]);

  // Debounced email check function
  const debouncedEmailCheck = useCallback(() => {
    return debounce(checkEmailAvailability, 800);
  }, [checkEmailAvailability]);

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    if (newEmail === '') {
      setEmailMessage('');
    } else if (isEmailValid(newEmail)) {
      debouncedEmailCheck(newEmail);
    } else {
      setEmailMessage('Please enter a valid email format.');
    }
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    
    // Validate Step 1: Basic Info
    if (!firstName.trim()) {
      showError('Please enter your first name.');
      return;
    }
    if (!grandfatherFirstName.trim()) {
      showError('Please enter your grandfather\'s first name.');
      return;
    }
    if (!placeOfBirth.trim()) {
      showError('Please enter your place of birth.');
      return;
    }

    setIsSubmitting(true);

    try {
      // 1. Get data key from server
      const keyResponse = await axios.post(`${API_CONFIG.BASE_URL}/kms/generate-data-key`, {}, {
        timeout: 60000
      });
      const { plaintext, ciphertext } = keyResponse.data;

      // 2. Encrypt form data locally
      const plaintextKeyBuffer = base64ToArrayBuffer(plaintext);
      const key = await window.crypto.subtle.importKey(
        "raw",
        plaintextKeyBuffer,
        "AES-CBC",
        true,
        ["encrypt", "decrypt"]
      );

      // Encrypt each field
      const encryptField = async (fieldValue) => {
        const fieldBuffer = Buffer.from(fieldValue, 'utf8');
        const fieldIv = window.crypto.getRandomValues(new Uint8Array(16));
        const encryptedFieldBuffer = await window.crypto.subtle.encrypt(
          { name: "AES-CBC", iv: fieldIv },
          key,
          fieldBuffer
        );
        const encryptedFieldHex = bufferToHex(encryptedFieldBuffer);
        const fieldIvHex = bufferToHex(fieldIv);
        return `${fieldIvHex}:${encryptedFieldHex}`;
      };

      const encryptedFirstName = await encryptField(firstName);
      const encryptedGrandfatherFirstName = await encryptField(grandfatherFirstName);
      const encryptedPlaceOfBirth = await encryptField(placeOfBirth);

      // 3. Submit step 1
      const response = await axios.post(`${API_CONFIG.BASE_URL}/registration/step1`, {
        encryptedDataKey: ciphertext,
        encryptedFirstName,
        encryptedGrandfatherFirstName,
        encryptedPlaceOfBirth,
        tenantId: tenantId || null
      });

      if (response.data.success) {
        setRegistrationId(response.data.registrationId);
        setStep(2);
        showSuccess('Step 1 completed! Now let\'s collect your contact information.');
      }
    } catch (error) {
      console.error('Step 1 failed:', error);
      showError('Failed to save step 1 data. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStep2Submit = async (e) => {
    e.preventDefault();
    
    // Validate Step 2: Contact Info
    if (!mothersFirstName.trim()) {
      showError('Please enter your mother\'s first name.');
      return;
    }
    if (!phoneNumber.trim()) {
      showError('Please enter your phone number.');
      return;
    }
    if (!email.trim() || !isEmailValid(email)) {
      showError('Please enter a valid email address.');
      return;
    }

    // Check email availability before proceeding
    const emailAvailable = await checkEmailAvailability(email);
    if (!emailAvailable) {
      showError('Please use a different email address.');
      return;
    }

    setIsSubmitting(true);

    try {
      // 1. Get data key from server
      const keyResponse = await axios.post(`${API_CONFIG.BASE_URL}/kms/generate-data-key`, {}, {
        timeout: 60000
      });
      const { plaintext, ciphertext } = keyResponse.data;

      // 2. Encrypt form data locally
      const plaintextKeyBuffer = base64ToArrayBuffer(plaintext);
      const key = await window.crypto.subtle.importKey(
        "raw",
        plaintextKeyBuffer,
        "AES-CBC",
        true,
        ["encrypt", "decrypt"]
      );

      // Encrypt each field
      const encryptField = async (fieldValue) => {
        const fieldBuffer = Buffer.from(fieldValue, 'utf8');
        const fieldIv = window.crypto.getRandomValues(new Uint8Array(16));
        const encryptedFieldBuffer = await window.crypto.subtle.encrypt(
          { name: "AES-CBC", iv: fieldIv },
          key,
          fieldBuffer
        );
        const encryptedFieldHex = bufferToHex(encryptedFieldBuffer);
        const fieldIvHex = bufferToHex(fieldIv);
        return `${fieldIvHex}:${encryptedFieldHex}`;
      };

      const encryptedMothersFirstName = await encryptField(mothersFirstName);
      const encryptedPhoneNumber = await encryptField(phoneNumber);
      const encryptedEmail = await encryptField(email);

      // 3. Submit step 2
      const response = await axios.post(`${API_CONFIG.BASE_URL}/registration/step2`, {
        registrationId,
        encryptedDataKey: ciphertext,
        encryptedMothersFirstName,
        encryptedPhoneNumber,
        encryptedEmail
      });

      if (response.data.success) {
        setStep(3);
        showSuccess('Step 2 completed! Finally, let\'s capture your biometric data.');
      }
    } catch (error) {
      console.error('Step 2 failed:', error);
      if (error.response?.data?.error === 'email_exists') {
        showError('This email is already registered. Please use a different email.');
      } else {
        showError('Failed to save step 2 data. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCapture = (imageSrc) => {
    setCapturedImage(imageSrc);
    setLivenessSessionId(null);
    showSuccess('Photo captured successfully! You can now complete the registration.');
  };

  const handleLivenessComplete = (sessionId) => {
    setLivenessSessionId(sessionId);
    setCapturedImage(null);
    showSuccess('Live face verification complete.');
  };

  const biometricReady = useLiveness ? Boolean(livenessSessionId) : Boolean(capturedImage);

  const handleStep3Submit = async (e) => {
    e.preventDefault();
    if (!biometricReady) {
      showError(
        useLiveness
          ? 'Please complete live face verification first'
          : 'Please capture a valid image before completing registration.'
      );
      return;
    }

    setIsSubmitting(true);

    try {
      // 1. Get data key from server
      const keyResponse = await axios.post(`${API_CONFIG.BASE_URL}/kms/generate-data-key`, {}, {
        timeout: 60000
      });
      const { plaintext, ciphertext } = keyResponse.data;

      // 2. Encrypt biometric data
      const plaintextKeyBuffer = base64ToArrayBuffer(plaintext);
      const key = await window.crypto.subtle.importKey(
        "raw",
        plaintextKeyBuffer,
        "AES-CBC",
        true,
        ["encrypt", "decrypt"]
      );

      const step3Payload = {
        registrationId,
        encryptedDataKey: ciphertext,
      };

      if (useLiveness && livenessSessionId) {
        step3Payload.livenessSessionId = livenessSessionId;
      } else {
        const base64Image = capturedImage.split(',')[1];
        const iv = window.crypto.getRandomValues(new Uint8Array(16));
        const imageBuffer = Buffer.from(base64Image, 'base64');
        const encryptedImageBuffer = await window.crypto.subtle.encrypt(
          { name: 'AES-CBC', iv: iv },
          key,
          imageBuffer
        );
        const encryptedImageHex = bufferToHex(encryptedImageBuffer);
        const ivHex = bufferToHex(iv);
        step3Payload.encryptedImageData = `${ivHex}:${encryptedImageHex}`;
      }

      const response = await axios.post(`${API_CONFIG.BASE_URL}/registration/step3`, step3Payload, {
        timeout: 120000,
      });

      if (response.data.success) {
        showSuccess('🎉 Registration completed successfully! Your SyberKey account has been created with secure biometric authentication.');
        
        // Show additional success message with redirect info
        
        // Redirect after showing success message
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }

    } catch (error) {
      console.error('Step 3 registration failed:', error);
      showError(getOnboardRegistrationErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="registration-container">
      
      {/* Step 1: Basic Info */}
      {step === 1 && (
        <form onSubmit={handleStep1Submit} className="registration-form">
          <h1 className="page-title-with-brand">
            <span className="page-title-with-brand__prefix">Register with </span>
            <SyberKeyWordmark />
          </h1>
          <h2>Step 1 of 3: Basic Information</h2>
          <div className="form-group">
            <label>First Name:</label>
            <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Grandfather's First Name:</label>
            <input type="text" value={grandfatherFirstName} onChange={(e) => setGrandfatherFirstName(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Place of Birth:</label>
            <input type="text" value={placeOfBirth} onChange={(e) => setPlaceOfBirth(e.target.value)} required />
          </div>
          {tenantId && (
            <input type="hidden" name="tenantId" value={tenantId} />
          )}
          <button 
            type="submit" 
            disabled={isSubmitting}
            className={isSubmitting ? 'loading' : ''}
          >
            {isSubmitting ? 'Saving...' : 'Next: Contact Information'}
          </button>
        </form>
      )}

      {/* Step 2: Contact Info */}
      {step === 2 && (
        <form onSubmit={handleStep2Submit} className="registration-form">
          <h1 className="page-title-with-brand">
            <span className="page-title-with-brand__prefix">Register with </span>
            <SyberKeyWordmark />
          </h1>
          <h2>Step 2 of 3: Contact Information</h2>
          <div className="form-group">
            <label>Mother's First Name:</label>
            <input type="text" value={mothersFirstName} onChange={(e) => setMothersFirstName(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Phone Number:</label>
            <input type="tel" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Email Address:</label>
            <input 
              type="email" 
              value={email} 
              onChange={handleEmailChange} 
              placeholder="Enter your email address"
              required 
            />
            {emailMessage && (
              <div className={`validation-message ${
                emailMessage.includes('✓') ? 'success' : 
                emailMessage.includes('✗') || emailMessage.includes('Error') ? 'error' : 
                'info'
              }`}>
                {emailMessage}
              </div>
            )}
          </div>
          <button 
            type="submit" 
            disabled={isSubmitting}
            className={isSubmitting ? 'loading' : ''}
          >
            {isSubmitting ? 'Saving...' : 'Next: Biometric Scan'}
          </button>
        </form>
      )}

      {/* Step 3: Biometric Scan */}
      {step === 3 && (
        <div className="registration-form">
          <h1 className="page-title-with-brand">
            <span className="page-title-with-brand__prefix">Register with </span>
            <SyberKeyWordmark />
          </h1>
          <h2>Step 3 of 3: {useLiveness ? 'Live Face Verification' : 'Biometric Scan'}</h2>
          {modeLoading ? (
            <p className="biometric-capture-status">Loading verification options…</p>
          ) : useLiveness ? (
            <>
              <BiometricLivenessCapture
                onComplete={handleLivenessComplete}
                onError={(msg) => showError(msg)}
              />
              {livenessSessionId && (
                <p className="biometric-capture-status">Live verification complete.</p>
              )}
            </>
          ) : (
            <>
              <BiometricCapture mode="capture" onCapture={handleCapture} />
              {capturedImage && (
                <div className="webcam-preview">
                  <h3>Captured Image Preview</h3>
                  <img src={capturedImage} alt="Captured" />
                </div>
              )}
            </>
          )}
          <button 
            type="button" 
            onClick={handleStep3Submit} 
            disabled={!biometricReady || isSubmitting || modeLoading}
            className={`registration-form__primary${isSubmitting ? ' loading' : ''}`}
          >
            {isSubmitting ? 'Creating Account...' : 'Complete Registration'}
          </button>
        </div>
      )}
    </div>
  );
}

export default Registration;
